import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Dialog — §9. Standard and danger.
 *
 * React Aria's `ModalOverlay` / `Modal` / `Dialog`: the focus trap, the scroll lock, the
 * backdrop, Escape, the return of focus to whatever opened it, and `aria-modal` are RAC's.
 *
 * `tone="danger"` renders `role="alertdialog"`, which is the difference between "here is a
 * form" and "read this before you answer" — the archive's permanent delete is the only
 * thing in the Studio that earns it.
 *
 * The `open` / `onClose` prop pair is kept rather than RAC's `DialogTrigger`, because every
 * confirm in the Studio is opened by application state (a spend decision, a delete that had
 * to be checked first) and not by a button sitting next to it.
 *
 * The children sit in their own `.d-body` box because that box is the one that scrolls. A
 * dialog is only as tall as the window allows, and when the form inside it is taller than
 * that — `File an issue` on a short screen — the title has to stay legible and `File it` has
 * to stay reachable. Scrolling the whole dialog would take both away.
 */
import { Dialog as AriaDialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { useEffect, useRef, useState } from 'react';
export function Dialog({ open, onClose, title, children, actions, cost, wide = false, tone = 'standard', }) {
    const body = useRef(null);
    const [scrolls, setScrolls] = useState(false);
    // A scroll box the keyboard cannot reach is half a fix, and a tabbable one that does not
    // scroll is a stop that leads nowhere — worse, it would take the focus RAC hands the
    // first field when the dialog opens. So the tab stop appears only while there is
    // something to scroll. Measured after every render, which is when the body's height can
    // have changed (an error line, a staged-file count), and observed as well, for the window
    // that gets shorter with the dialog already open.
    useEffect(() => {
        const element = body.current;
        if (element === null)
            return;
        const measure = () => {
            setScrolls(element.scrollHeight > element.clientHeight);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    });
    return (_jsx(ModalOverlay, { className: "dialog-scrim", isOpen: open, onOpenChange: (next) => {
            if (!next)
                onClose();
        }, isDismissable: true, children: _jsx(Modal, { className: "dialog-shell", children: _jsxs(AriaDialog, { className: [
                    'dialog',
                    tone === 'danger' ? 'dialog-danger' : null,
                    wide ? 'dialog-wide' : null,
                ]
                    .filter(Boolean)
                    .join(' '), role: tone === 'danger' ? 'alertdialog' : 'dialog', children: [tone === 'danger' && _jsx("span", { className: "d-rule", "aria-hidden": "true" }), _jsx(Heading, { slot: "title", level: 4, children: title }), children !== undefined && (_jsx("div", { className: "d-body", ref: body, ...(scrolls ? { tabIndex: 0 } : {}), children: children })), _jsxs("div", { className: "d-acts", children: [cost !== undefined && _jsx("span", { className: "cost", children: cost }), actions] })] }) }) }));
}
//# sourceMappingURL=Dialog.js.map